import { Box, Button, Container, Typography } from "@material-ui/core"
import { getGroupPurchaseGetGroupPurchaseSettings } from "api"
import { formatCurrencyWithSybmol } from "common/utils/formatters"
import GenericButton from "components/GenericButton/GenericButton"
import { GenericInput } from "components/GenericInput/GenericInput"
import {
  GroupPurchasesSteps,
  useGroupPurchasesContext,
} from "components/PurchasePage/GroupPurchasesContext"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { invalid, regex, required } from "components/Reusable/Bit/FormikSchema"
import FormInput from "components/Reusable/FormsComp/FormInput"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import * as yup from "yup"
import GroupPurchasesHeader from "./GroupPurchasesHeader"
import GroupPurchasesPageCover from "./GroupPurchasesPageCover"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledWrapper = styled(Container)`
  margin-top: 80px;
  padding-right: 0px;
  padding-left: 0px;

  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row-reverse;
    margin-top: 120px;
    justify-content: space-between;
  }
`

export const StyledInputLabelBottom = styled(Typography).attrs({
  variant: "h3",
})`
  color: #000000;
  margin-top: 12px;
  margin-bottom: 110px;
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  text-align: right;
  font-family: "NarkisBlock-Thin";
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 56px;
  }
`

export const StyledFormInput = styled(FormInput)`
  .MuiInputBase-multiline {
    height: 80px;
    fieldset {
      height: 80px;
      top: 0px;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      height: 120px;
      width: 680px;
      fieldset {
        height: 120px;
        top: 0px;
      }
    }
  }
  .MuiOutlinedInput-adornedEnd {
    cursor: pointer;
    padding-left: unset;
    .MuiIconButton-root {
      padding: 14px;
      &:hover {
        background-color: transparent;
      }
    }
  }
  .MuiOutlinedInput-root.Mui-error {
    background-color: #f7f7f7;
  }
  .MuiOutlinedInput-root:not(.Mui-error):hover
    .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }
  .Mui-focused {
    background-color: white;
  }
  .MuiOutlinedInput-input {
    padding: 14px 8px;
    color: #000000;
    font-size: 20px;
    font-weight: 400;
    line-height: 22px;
    text-align: right;
    ::placeholder {
      color: #00000066;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      text-align: right;
      opacity: 1;
    }
    :focus::placeholder {
      color: transparent;
    }
  }
  .MuiFormHelperText-root:not(.Mui-error) {
    display: none;
  }
`

export const StyledButtonWrapper = styled(Box)`
  width: 328px;
  margin-bottom: 40px;
  margin-top: 86px;
  position: relative;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 20px;
    position: fixed;
    bottom: 0;
    right: 0;
    width: calc(100% - 32px);
    border-radius: 10px;
    box-shadow: 0 -5px 10px 0 hsla(0, 0%, 0%, 0.12);
    background-color: #fff;
    padding: 12px 16px 10px 16px;
    margin-top: 0px;
    z-index: 999;
    margin-bottom: 0px;
  }
  box-sizing: content-box;
`

export const StyledButton = styled(Button).attrs({ variant: "contained" })<{
  $color: string
  $background: string
}>`
  color: ${(p) => p.$color};
  background-color: ${(p) => p.$background};
  height: 48px;
  width: 100%;
  &:hover {
    background-color: ${(p) => p.$background} !important;
    color: ${(p) => p.$color};
  }
  &:disabled {
    background-color: ${(p) => p.theme.colors.disabled} !important;
    color: #757575 !important;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    border-radius: 0px;
  }
`

const StyledMain = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding-left: 60px;
    padding-right: 60px;
  }
`

export const StyledFieldWrapper = styled(Box)`
  display: flex;

  ${(p) => p.theme.breakpoints.down("sm")} {
    > * {
      width: 100%;
    }
  }
  .MuiFormLabel-asterisk {
    display: none;
  }
`

const GroupPurchasesAmountPage = () => {
  const amountInputRef = useRef(null)
  const {
    groupPurchasesData,
    setGroupPurchasesData,
    setGroupPurchasesSteps,
    setPrevPageId,
    setPrevPath,
  } = useGroupPurchasesContext()

  // Enables hitting back button while navigating through SPA style page
  setPrevPageId(1)
  setPrevPath("/groupPurchases")

  const groupPurchasesInitialData = {
    price: groupPurchasesData?.price || "",
  }

  const formikRef = useRef<any>(null)

  const saveGroupPurchasesData = () => {
    if (formikRef.current) {
      const formValues = formikRef.current?.values
      setGroupPurchasesData({ ...groupPurchasesData, ...formValues })
    }
  }

  const priceRef = useRef(null)

  const [maxAmount, setMaxAmount] = useState(0)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getGroupPurchaseGetGroupPurchaseSettings()
        setMaxAmount(response?.data?.body?.maxPrice)
        if (response && groupPurchasesData.price) {
          if (priceRef.current) {
            setTimeout(() => {
              priceRef.current.getElementsByTagName("input")[0].focus()
              setTimeout(() => {
                priceRef.current.getElementsByTagName("input")[0].blur()
              }, 50)
            }, 50)
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()

    // Scroll to top on page load
    if (window) {
      window.scrollTo(0, 0)
    }
  }, [])

  const schemaGroupPurchasesAmount = yup
    .object({
      price: yup
        .string()
        .matches(regex.amount, invalid.amount)
        .test("min-value", "צריך להיות לפחות 10 ₪", (value) => {
          const numericValue = parseFloat(value?.replace(/[^\d.-]/g, ""))
          return numericValue > 9
        })
        .test("max-value", invalid.groupPurchaseAmount, (value) => {
          const numericValue = parseFloat(value?.replace(/[^\d.-]/g, ""))
          return numericValue <= maxAmount
        })
        .required(required.default),
    })
    .defined()

  return (
    <StyledWrapper>
      <GroupPurchasesPageCover backBtnOnClick={saveGroupPurchasesData} />
      <StyledMain>
        <GroupPurchasesHeader
          mainTitle={"כמה כסף לאסוף מכל אחד?"}
          subTitle={
            "כל חבר/חברה שיקבלו הזמנה לקבוצה יראו את הסכום שקבעת, אבל עדיין יוכלו לבחור סכום אחר"
          }
          component="h2"
          backBtnOnClick={saveGroupPurchasesData}
        />
        <FormikForm
          innerRef={formikRef}
          validationSchema={schemaGroupPurchasesAmount}
          initialValues={groupPurchasesInitialData}
          onSubmit={async (values) => {
            setGroupPurchasesData({
              ...groupPurchasesData,
              price:
                typeof values.price === "string"
                  ? parseInt(values.price.split("₪")?.[1])
                  : values.price,
            })
            setGroupPurchasesSteps(GroupPurchasesSteps.LeaderDetails)
          }}
          render={(formik) => {
            return (
              <>
                <StyledFieldWrapper>
                  <GenericInput
                    innerRef={priceRef}
                    name="price"
                    label="הזנת סכום (*חובה)"
                    $width="328"
                    $mobileWidth="100%"
                    type="text"
                    maxLength={30}
                    topMargin="0"
                    placeholder="סכום לאסוף מכל משתתף/ת"
                    inputRef={amountInputRef}
                    autoComplete="off"
                    numeric={true}
                    required
                    aria-required={true}
                    value={formatCurrencyWithSybmol(formik.values.price)}
                  />
                </StyledFieldWrapper>
                <StyledInputLabelBottom>
                  ניתן לרכוש עד לסכום של {maxAmount} ש”ח
                </StyledInputLabelBottom>
                <StyledButtonWrapper>
                  <GenericButton
                    type="submit"
                    btnType="filled"
                    isdisabled={
                      formik?.isSubmitting ||
                      Object.keys(formik?.errors).length > 0
                    }
                  >
                    המשך
                  </GenericButton>
                </StyledButtonWrapper>
              </>
            )
          }}
        ></FormikForm>
      </StyledMain>
    </StyledWrapper>
  )
}

export default GroupPurchasesAmountPage
